import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
    Store, Phone, MapPin, Navigation, Upload, CheckCircle2, AlertCircle, Clock, Send, 
    MessageSquare, Mail, RefreshCw, X, Eye, ShieldCheck, Check, ArrowRight, ArrowLeft,
    Sparkles, User, ExternalLink, Image as ImageIcon, Camera, CheckCircle, ChevronLeft,
    Home, BellRing, ShieldAlert, Ban
} from 'lucide-react';
import { 
    submitVendorApplication, 
    getVendorApplicationStatus, 
    uploadVendorDocument, 
    getSupportConversation,
    sendSupportMessage,
    getActiveCountries,
    getUserId
} from '../api/client';
import { socket } from '../api/socket';
import { useAuth } from '../context/AuthContext';
import { isRTL } from '../utils/translations';
import Spinner from '../components/Spinner';

// Clean Modern Luxury Palette (NO GOLD, NO YELLOW LINES)
const BG_COLOR = '#0B0F17';
const DARK_CARD_BG = '#131B2A';
const BORDER_STROKE = 'rgba(255, 255, 255, 0.09)';
const BORDER_HOVER = 'rgba(255, 255, 255, 0.2)';
const ACCENT_WHITE = '#FFFFFF';
const TEXT_MUTED = '#94A3B8';
const EMERALD_COLOR = '#10B981';
const EMERALD_DARK = '#064E3B';
const RED_BG = '#2C1010';
const RED_BORDER = '#EF4444';

// Synthesize pleasant success chime when approved
const playApprovalChime = () => {
    try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        const ctx = new AudioContext();
        const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
        notes.forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.12);
            gain.gain.setValueAtTime(0.18, ctx.currentTime + i * 0.12);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.12 + 0.4);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(ctx.currentTime + i * 0.12);
            osc.stop(ctx.currentTime + i * 0.12 + 0.45);
        });
    } catch (_) {}
};

export default function VendorApplicationPage() {
    const navigate = useNavigate();
    const { user, isAuthenticated, loading: authLoading } = useAuth();
    const rtl = isRTL();
    const fileInputRef = useRef(null);
    const chatEndRef = useRef(null);

    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [status, setStatus] = useState(null); // 'PENDING' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED'
    const [rejectionReason, setRejectionReason] = useState('');
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [showLiveApprovedModal, setShowLiveApprovedModal] = useState(false);

    // Form fields mirroring VendorApplicationScreen.kt
    const [storeName, setStoreName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [latitude, setLatitude] = useState(null);
    const [longitude, setLongitude] = useState(null);
    const [mapLocationUrl, setMapLocationUrl] = useState('');
    const [locationPermissionGranted, setLocationPermissionGranted] = useState(false);
    const [storeLocationDesc, setStoreLocationDesc] = useState('');
    const [isLocating, setIsLocating] = useState(false);
    const [applicantGender, setApplicantGender] = useState('MALE');
    const [applicantAge, setApplicantAge] = useState('');
    const [countryCode, setCountryCode] = useState('SA');
    const [city, setCity] = useState('');

    const recentInvoicesMonth = new Date().toLocaleDateString('ar-SA', { month: 'long', year: 'numeric' }) || 'أكتوبر 2026';
    const [invoiceItems, setInvoiceItems] = useState([]);
    const [isUploadingInvoices, setIsUploadingInvoices] = useState(false);
    const [previewingInvoiceUrl, setPreviewingInvoiceUrl] = useState(null);

    // Live review chat states
    const [recentChatMessages, setRecentChatMessages] = useState([]);
    const [chatReplyText, setChatReplyText] = useState('');
    const [isSendingChatReply, setIsSendingChatReply] = useState(false);

    // Initial Fetch
    useEffect(() => {
        if (authLoading) return;

        if (!isAuthenticated) {
            navigate('/login?redirect=' + encodeURIComponent('/vendor/apply'));
            return;
        }

        const fetchStatus = async () => {
            setIsLoading(true);
            try {
                const geoContext = JSON.parse(localStorage.getItem('luxvono_geo_context') || '{}');
                const defaultCountry = geoContext.country || 'SA';
                setCountryCode(defaultCountry);

                const res = await getVendorApplicationStatus().catch(() => null);
                const data = res?.data && res.data !== res ? res.data : (res?.status ? res : (res?.data || null));
                if (data && (data.status || data.id)) {
                    setStatus(data.status || null);
                    setStoreName(data.storeName || data.businessName || '');
                    setPhoneNumber(data.phoneNumber || '');
                    setStoreLocationDesc(data.storeLocationDesc || '');
                    if (data.latitude) setLatitude(data.latitude);
                    if (data.longitude) setLongitude(data.longitude);
                    if (data.mapLocationUrl) setMapLocationUrl(data.mapLocationUrl);
                    if (data.countryCode) setCountryCode(data.countryCode);
                    if (data.city) setCity(data.city);
                    if (data.applicantGender) setApplicantGender(data.applicantGender);
                    if (data.applicantAge) setApplicantAge(String(data.applicantAge));
                    if (data.rejectionReason) setRejectionReason(data.rejectionReason);

                    // If already approved, mark local storage
                    if (data.status === 'APPROVED') {
                        localStorage.setItem('is_seller', 'true');
                        localStorage.setItem('is_seller_mode_active', 'true');
                    } else if (data.status === 'BANNED' || data.status === 'SUSPENDED') {
                        localStorage.removeItem('is_seller');
                        localStorage.removeItem('is_seller_mode_active');
                    }

                    // Parse existing invoices if available
                    if (data.recentInvoicesUrls) {
                        try {
                            const parsed = JSON.parse(data.recentInvoicesUrls);
                            if (Array.isArray(parsed) && parsed.length > 0) {
                                setInvoiceItems(parsed.map(url => ({
                                    id: Math.random().toString(36).substring(2, 9),
                                    file: null,
                                    previewUrl: url,
                                    remoteUrl: url,
                                    isUploading: false,
                                    isSuccess: true,
                                    isError: false
                                })));
                            }
                        } catch (_) {}
                    }
                }
            } catch (err) {
                console.error('Error fetching application status', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchStatus();
    }, [isAuthenticated, navigate]);

    // Live Socket listener for instant approval
    useEffect(() => {
        const handleLiveApproval = (payload) => {
            const currentUid = user?.id || getUserId() || localStorage.getItem('userId');
            if (!payload || !payload.userId || String(payload.userId) === String(currentUid)) {
                localStorage.setItem('is_seller', 'true');
                localStorage.setItem('is_seller_mode_active', 'true');
                setStatus('APPROVED');
                setShowLiveApprovedModal(true);
                playApprovalChime();
            }
        };

        socket.on('vendor-application-approved', handleLiveApproval);
        socket.on('vendorApplicationStatusEvent', handleLiveApproval);
        socket.on('vendor_approved', handleLiveApproval);

        return () => {
            socket.off('vendor-application-approved', handleLiveApproval);
            socket.off('vendorApplicationStatusEvent', handleLiveApproval);
            socket.off('vendor_approved', handleLiveApproval);
        };
    }, [user]);

    // Live Poll Loop for Review Chat & Live Instant Approval (Every 2.5 seconds while PENDING/UNDER_REVIEW)
    useEffect(() => {
        let isMounted = true;
        const currentUserId = user?.id || getUserId() || localStorage.getItem('userId');
        const targetId = currentUserId ? String(currentUserId) : 'me';

        const pollReviewData = async () => {
            try {
                // 1. Fetch live application status
                const appRes = await getVendorApplicationStatus().catch(() => null);
                const appData = appRes?.data && appRes.data !== appRes ? appRes.data : (appRes?.status ? appRes : (appRes?.data || null));
                if (isMounted && appData) {
                    const newStatus = appData.status;
                    if (newStatus && newStatus !== status) {
                        if (newStatus === 'APPROVED') {
                            localStorage.setItem('is_seller', 'true');
                            localStorage.setItem('is_seller_mode_active', 'true');
                            setStatus('APPROVED');
                            setShowLiveApprovedModal(true);
                            playApprovalChime();
                        } else if (newStatus === 'REJECTED') {
                            setStatus('REJECTED');
                            setRejectionReason(appData.rejectionReason || '');
                            setErrorMessage(rtl 
                                ? `تم رفض الطلب: ${appData.rejectionReason || 'يرجى مراجعة وتحديث البيانات'}` 
                                : `Application rejected: ${appData.rejectionReason || 'Please review and update'}`);
                        } else if (newStatus === 'BANNED' || newStatus === 'SUSPENDED') {
                            localStorage.removeItem('is_seller');
                            localStorage.removeItem('is_seller_mode_active');
                            setStatus('BANNED');
                            setRejectionReason(appData.rejectionReason || 'مخالفة معايير الجودة والاشتراطات التجارية والامتثال');
                        } else {
                            setStatus(newStatus);
                        }
                    }
                }

                // 2. Fetch live review/support chat messages if pending or banned
                if (status === 'PENDING' || status === 'UNDER_REVIEW' || status === 'BANNED' || status === 'SUSPENDED') {
                    const chatRes = await getSupportConversation(targetId).catch(() => null);
                    if (isMounted && chatRes) {
                        const dataObj = chatRes.data || chatRes;
                        const msgs = dataObj.messages || (Array.isArray(dataObj) ? dataObj : []);
                        if (Array.isArray(msgs)) {
                            setRecentChatMessages(msgs);
                        }
                    }
                }
            } catch (err) {
                console.warn('Poll review error', err);
            }
        };

        const intervalId = setInterval(pollReviewData, 2500);
        return () => {
            isMounted = false;
            clearInterval(intervalId);
        };
    }, [status, user, rtl]);

    // Scroll chat to bottom on new messages
    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [recentChatMessages]);

    // Send Quick Review Chat Reply
    const handleSendReviewReply = async (e) => {
        if (e) e.preventDefault();
        const text = chatReplyText.trim();
        if (!text || isSendingChatReply) return;

        setIsSendingChatReply(true);
        const currentUserId = user?.id || getUserId() || localStorage.getItem('userId');

        try {
            await sendSupportMessage({
                userId: currentUserId,
                senderType: 'user',
                senderId: currentUserId,
                message: text,
                content: text
            });

            setChatReplyText('');
            setRecentChatMessages(prev => [
                ...prev,
                {
                    senderType: 'user',
                    content: text,
                    message: text,
                    senderName: rtl ? 'أنت' : 'You',
                    createdAt: new Date().toISOString()
                }
            ]);
        } catch (err) {
            console.error('Failed to send review chat message', err);
        } finally {
            setIsSendingChatReply(false);
        }
    };

    // Geolocation capture
    const handleCaptureLocation = () => {
        if (!navigator.geolocation) {
            alert(rtl ? 'متصفحك لا يدعم تحديد الموقع الجغرافي (GPS)' : 'Geolocation is not supported by your browser');
            return;
        }

        setIsLocating(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;
                setLatitude(lat);
                setLongitude(lng);
                setLocationPermissionGranted(true);
                setMapLocationUrl(`https://www.google.com/maps?q=${lat},${lng}`);
                setIsLocating(false);
            },
            (err) => {
                setIsLocating(false);
                alert(rtl 
                    ? 'تعذر تحديد الموقع الجغرافي. يرجى تفعيل إذن الوصول للموقع في المتصفح أو إدخال وصف العنوان يدوياً.' 
                    : 'Location permission was denied or unavailable. Please type your location description manually.');
            },
            { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
        );
    };

    // Invoices Multi-Image Picker & Live Upload Pipeline
    const handleSelectInvoices = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        setErrorMessage(null);
        const newItems = files.map(file => ({
            id: Math.random().toString(36).substring(2, 9),
            file,
            previewUrl: URL.createObjectURL(file),
            remoteUrl: null,
            isUploading: true,
            isSuccess: false,
            isError: false
        }));

        setInvoiceItems(prev => [...prev, ...newItems]);
        setIsUploadingInvoices(true);

        for (const item of newItems) {
            try {
                const res = await uploadVendorDocument(item.file, 'invoice');
                const rawUrl = res?.data || res;
                const finalUrl = typeof rawUrl === 'string' ? rawUrl : (rawUrl?.data || rawUrl?.url || rawUrl?.fileUrl || '');

                if (finalUrl) {
                    const resolved = finalUrl.startsWith('/') ? `https://luxvono.com${finalUrl}` : finalUrl;
                    setInvoiceItems(currList => currList.map(curr => 
                        curr.id === item.id 
                            ? { ...curr, remoteUrl: resolved, isUploading: false, isSuccess: true, isError: false } 
                            : curr
                    ));
                } else {
                    setInvoiceItems(currList => currList.map(curr => 
                        curr.id === item.id 
                            ? { ...curr, isUploading: false, isSuccess: false, isError: true } 
                            : curr
                    ));
                }
            } catch (err) {
                console.error('Invoice upload failed', err);
                setInvoiceItems(currList => currList.map(curr => 
                    curr.id === item.id 
                        ? { ...curr, isUploading: false, isSuccess: false, isError: true } 
                        : curr
                ));
            }
        }

        setIsUploadingInvoices(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // Retry individual failed invoice upload
    const handleRetryUpload = async (item) => {
        if (!item.file) return;

        setInvoiceItems(currList => currList.map(c => 
            c.id === item.id ? { ...c, isUploading: true, isError: false } : c
        ));

        try {
            const res = await uploadVendorDocument(item.file, 'invoice');
            const rawUrl = res?.data || res;
            const finalUrl = typeof rawUrl === 'string' ? rawUrl : (rawUrl?.data || rawUrl?.url || rawUrl?.fileUrl || '');

            if (finalUrl) {
                const resolved = finalUrl.startsWith('/') ? `https://luxvono.com${finalUrl}` : finalUrl;
                setInvoiceItems(currList => currList.map(curr => 
                    curr.id === item.id 
                        ? { ...curr, remoteUrl: resolved, isUploading: false, isSuccess: true, isError: false } 
                        : curr
                ));
            } else {
                setInvoiceItems(currList => currList.map(curr => 
                    curr.id === item.id 
                        ? { ...curr, isUploading: false, isSuccess: false, isError: true } 
                        : curr
                ));
            }
        } catch (err) {
            setInvoiceItems(currList => currList.map(curr => 
                curr.id === item.id 
                    ? { ...curr, isUploading: false, isSuccess: false, isError: true } 
                    : curr
            ));
        }
    };

    // Delete invoice item
    const handleRemoveInvoice = (idToRemove) => {
        setInvoiceItems(curr => curr.filter(item => item.id !== idToRemove));
    };

    // Submit Application mirroring mobile app payload
    const handleSubmitApplication = async () => {
        const cleanStoreName = storeName.trim();
        const cleanPhone = phoneNumber.trim();

        if (!cleanStoreName || !cleanPhone) {
            setErrorMessage(rtl 
                ? 'يرجى إدخال اسم المتجر ورقم الهاتف الخاص للتواصل والتحقق' 
                : 'Please enter Store Name and Personal Mobile Number');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        const validRemoteUrls = invoiceItems.map(item => item.remoteUrl).filter(Boolean);
        if (validRemoteUrls.length === 0) {
            setErrorMessage(rtl 
                ? 'يرجى إرفاق صور آخر فواتير بيع للإثبات والتحقق قبل الإرسال' 
                : 'Please upload recent sales invoices for store verification');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        setErrorMessage(null);
        setIsSubmitting(true);

        try {
            const payload = {
                businessName: cleanStoreName,
                businessNameAr: cleanStoreName,
                businessType: 'INDIVIDUAL',
                storeName: cleanStoreName,
                storeLocationDesc: storeLocationDesc.trim() || (rtl ? 'موقع المحل الجغرافي المحدد' : 'Pinned Shop Location'),
                latitude: latitude,
                longitude: longitude,
                mapLocationUrl: mapLocationUrl,
                locationPermissionGranted: locationPermissionGranted,
                applicantGender: applicantGender,
                applicantAge: applicantAge ? parseInt(applicantAge, 10) : null,
                recentInvoicesMonth: recentInvoicesMonth,
                recentInvoicesUrls: JSON.stringify(validRemoteUrls),
                commercialRegistration: '',
                taxNumber: '',
                countryCode: countryCode || 'SA',
                city: city,
                phoneNumber: cleanPhone,
                bankName: '',
                bankAccountNumber: ''
            };

            const res = await submitVendorApplication(payload);
            const appData = res?.data || res;
            if (appData) {
                setStatus('PENDING');
                setSuccessMessage(rtl 
                    ? 'تم إرسال طلبك بنجاح! طلبك الآن قيد المراجعة والتحقق.' 
                    : 'Application submitted successfully! Under review.');
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (err) {
            setErrorMessage(err.response?.data?.message || (rtl 
                ? 'فشل إرسال الطلب، يرجى المحاولة لاحقاً' 
                : 'Submission failed. Please try again.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div style={{
                minHeight: '100vh',
                backgroundColor: BG_COLOR,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <Spinner />
            </div>
        );
    }

    // ─────────────────────────────────────────────────────────────
    // 1. APPROVED STATE
    // ─────────────────────────────────────────────────────────────
    if (status === 'APPROVED') {
        const clean = (storeName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const sellerBusinessEmail = clean ? `${clean}@luxvono.com` : 'vendor@luxvono.com';

        return (
            <div style={{
                minHeight: '100vh',
                backgroundColor: BG_COLOR,
                color: '#FFFFFF',
                direction: rtl ? 'rtl' : 'ltr',
                padding: '32px 16px 80px',
                display: 'flex',
                justifyContent: 'center'
            }}>
                <div style={{ maxWidth: '640px', width: '100%', textAlign: 'center' }}>
                    {/* Top Home Navigation Bar */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '28px'
                    }}>
                        <Link
                            to="/"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                backgroundColor: '#1A2333',
                                border: `1px solid ${BORDER_STROKE}`,
                                borderRadius: '10px',
                                padding: '8px 16px',
                                color: '#FFFFFF',
                                textDecoration: 'none',
                                fontSize: '13px',
                                fontWeight: '700'
                            }}
                        >
                            <Home size={16} />
                            <span>{rtl ? 'العودة للشاشة الرئيسية' : 'Return to Home'}</span>
                        </Link>
                        <div style={{ fontSize: '13px', color: '#94A3B8', fontWeight: '600' }}>
                            LUXVONO VERIFIED VENDOR
                        </div>
                    </div>

                    <div style={{
                        width: '84px',
                        height: '84px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(16, 185, 129, 0.15)',
                        border: `2px solid ${EMERALD_COLOR}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px',
                        boxShadow: '0 0 32px rgba(16, 185, 129, 0.35)'
                    }}>
                        <CheckCircle size={48} color={EMERALD_COLOR} />
                    </div>

                    <h1 style={{
                        fontSize: '28px',
                        fontWeight: '800',
                        color: '#FFFFFF',
                        marginBottom: '12px'
                    }}>
                        {rtl ? 'تم قبولك واعتماد حسابك كمورد رسمي 🎉' : 'Official Vendor Account Approved 🎉'}
                    </h1>

                    <p style={{
                        color: '#9CA3AF',
                        fontSize: '15px',
                        lineHeight: '1.7',
                        marginBottom: '28px'
                    }}>
                        {rtl 
                            ? 'مبارك! حسابك مفعّل ويمكنك الآن رفع منتجاتك وإدارة مخزونك وأسعارك والتحكم بمتجرك.' 
                            : 'Congratulations! Your account is verified. You can now manage your catalog, prices, and stock.'}
                    </p>

                    {/* Dedicated Corporate Email Card */}
                    <div style={{
                        backgroundColor: DARK_CARD_BG,
                        border: `1px solid ${BORDER_STROKE}`,
                        borderRadius: '16px',
                        padding: '24px',
                        textAlign: rtl ? 'right' : 'left',
                        marginBottom: '24px',
                        boxShadow: '0 12px 36px rgba(0, 0, 0, 0.5)'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '12px'
                        }}>
                            <Mail size={22} color={EMERALD_COLOR} />
                            <h3 style={{
                                margin: 0,
                                fontSize: '15px',
                                fontWeight: '700',
                                color: '#FFFFFF'
                            }}>
                                {rtl ? 'البريد الإلكتروني المهني المعتمد لمتجرك ✉️' : 'Official Corporate Email ✉️'}
                            </h3>
                        </div>

                        <p style={{
                            color: '#CBD5E1',
                            fontSize: '13px',
                            lineHeight: '1.6',
                            marginBottom: '16px'
                        }}>
                            {rtl 
                                ? 'تم تخصيص وتفعيل صندوق بريد مؤسسي رسمي لمتجرك لاستقبال مراسلات المشترين وإشعارات المنصة:' 
                                : 'Your dedicated corporate business email is active to receive buyer inquiries and notifications:'}
                        </p>

                        <div style={{
                            backgroundColor: '#0F172A',
                            border: `1px solid ${BORDER_STROKE}`,
                            borderRadius: '10px',
                            padding: '14px',
                            textAlign: 'center',
                            color: '#FFFFFF',
                            fontWeight: '700',
                            fontSize: '17px',
                            letterSpacing: '0.5px',
                            marginBottom: '14px'
                        }}>
                            {sellerBusinessEmail}
                        </div>

                        <div style={{
                            fontSize: '12px',
                            color: '#888888',
                            textAlign: 'center'
                        }}>
                            {rtl 
                                ? '🌐 بوابة بريد الويب: webmail.luxvono.com | خادم البريد: mail.luxvono.com' 
                                : '🌐 Webmail portal: webmail.luxvono.com | Mail server: mail.luxvono.com'}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <Link
                            to="/seller/dashboard"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '10px',
                                backgroundColor: '#FFFFFF',
                                color: '#0A0F1A',
                                height: '52px',
                                borderRadius: '12px',
                                fontWeight: '800',
                                fontSize: '16px',
                                textDecoration: 'none',
                                boxShadow: '0 8px 24px rgba(255, 255, 255, 0.15)',
                                transition: 'all 0.25s ease'
                            }}
                        >
                            <Store size={20} />
                            <span>{rtl ? 'الانتقال إلى لوحة تحكم المورد وإضافة المنتجات' : 'Go to Vendor Dashboard & Upload Products'}</span>
                        </Link>

                        <Link
                            to="/"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                backgroundColor: '#1A2333',
                                border: `1px solid ${BORDER_STROKE}`,
                                color: '#FFFFFF',
                                height: '48px',
                                borderRadius: '12px',
                                fontWeight: '700',
                                fontSize: '14px',
                                textDecoration: 'none'
                            }}
                        >
                            <Home size={18} />
                            <span>{rtl ? 'العودة للشاشة الرئيسية' : 'Return to Home Screen'}</span>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // ─────────────────────────────────────────────────────────────
    // 1.5. BANNED STATE (تم حظر حسابك - يرجى التواصل مع الدعم الفني)
    // ─────────────────────────────────────────────────────────────
    if (status === 'BANNED' || status === 'SUSPENDED') {
        const banDisplayReason = rejectionReason || 'مخالفة معايير الجودة والاشتراطات التجارية والامتثال للسياسات.';

        return (
            <div style={{
                minHeight: '100vh',
                backgroundColor: BG_COLOR,
                color: '#FFFFFF',
                direction: rtl ? 'rtl' : 'ltr',
                padding: '32px 16px 80px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start'
            }}>
                <div style={{ maxWidth: '640px', width: '100%' }}>
                    {/* Top Home Navigation Bar */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '20px'
                    }}>
                        <Link
                            to="/"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                backgroundColor: '#1A2333',
                                border: `1px solid ${BORDER_STROKE}`,
                                borderRadius: '10px',
                                padding: '8px 16px',
                                color: '#FFFFFF',
                                textDecoration: 'none',
                                fontSize: '13px',
                                fontWeight: '700'
                            }}
                        >
                            <Home size={16} />
                            <span>{rtl ? 'العودة للمتجر كمشتري' : 'Browse Store as Customer'}</span>
                        </Link>
                        <div style={{ fontSize: '13px', color: '#EF4444', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <ShieldAlert size={16} color="#EF4444" />
                            <span>{rtl ? 'حساب تجاري محظور' : 'ACCOUNT BANNED'}</span>
                        </div>
                    </div>

                    {/* Main Red Ban Banner Card */}
                    <div style={{
                        backgroundColor: '#161118',
                        border: '1px solid rgba(239, 68, 68, 0.4)',
                        borderRadius: '24px',
                        padding: '32px 24px',
                        textAlign: 'center',
                        marginBottom: '20px',
                        boxShadow: '0 20px 50px rgba(239, 68, 68, 0.15)'
                    }}>
                        <div style={{
                            width: '76px',
                            height: '76px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(239, 68, 68, 0.15)',
                            border: '2px solid rgba(239, 68, 68, 0.45)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 18px',
                            boxShadow: '0 0 30px rgba(239, 68, 68, 0.3)'
                        }}>
                            <ShieldAlert size={40} color="#EF4444" />
                        </div>

                        <h1 style={{
                            fontSize: '24px',
                            fontWeight: '900',
                            color: '#F87171',
                            margin: '0 0 10px 0'
                        }}>
                            {rtl ? 'تم حظر حسابك التجاري' : 'Your Vendor Account has been Banned'}
                        </h1>

                        <p style={{
                            fontSize: '15px',
                            color: '#E2E8F0',
                            lineHeight: '1.6',
                            margin: '0 0 20px 0'
                        }}>
                            {rtl 
                                ? 'نأسف لإبلاغك بأنه تم إيقاف نشاط متجرك وسحب صلاحيات البيع على منصة LUXVONO من قبل الإدارة.' 
                                : 'Your seller privileges and store have been suspended by the LUXVONO platform administration.'}
                        </p>

                        {/* Ban Reason Box */}
                        <div style={{
                            backgroundColor: 'rgba(239, 68, 68, 0.08)',
                            border: '1px solid rgba(239, 68, 68, 0.25)',
                            borderRadius: '14px',
                            padding: '16px 20px',
                            textAlign: rtl ? 'right' : 'left',
                            marginBottom: '20px'
                        }}>
                            <div style={{ fontSize: '12px', fontWeight: '800', color: '#FCA5A5', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <AlertCircle size={14} color="#EF4444" />
                                <span>{rtl ? 'سبب الحظر والإيقاف:' : 'Ban Reason:'}</span>
                            </div>
                            <div style={{ fontSize: '14px', color: '#FFFFFF', fontWeight: '700', lineHeight: '1.5' }}>
                                {banDisplayReason}
                            </div>
                        </div>

                        {/* Action Instructions */}
                        <div style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.04)',
                            border: '1px solid rgba(255, 255, 255, 0.08)',
                            borderRadius: '14px',
                            padding: '14px 18px',
                            fontSize: '13px',
                            color: '#CBD5E1',
                            lineHeight: '1.6',
                            marginBottom: '10px'
                        }}>
                            {rtl 
                                ? '⚠️ يرجى التواصل مع الدعم الفني لحل المشكلة أو تقديم استئناف واستفسار لإعادة تنشيط الحساب.' 
                                : '⚠️ Please contact technical support to submit an inquiry or appeal to restore your account.'}
                        </div>
                    </div>

                    {/* Real-time Support Chat with Review/Support Team */}
                    <div style={{
                        backgroundColor: DARK_CARD_BG,
                        border: `1px solid ${BORDER_STROKE}`,
                        borderRadius: '20px',
                        padding: '20px',
                        marginBottom: '20px'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '14px',
                            paddingBottom: '12px',
                            borderBottom: `1px solid ${BORDER_STROKE}`
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <MessageSquare size={18} color="#38BDF8" />
                                <span style={{ fontWeight: '800', fontSize: '14px', color: '#FFFFFF' }}>
                                    {rtl ? 'مراسلة الدعم الفني والإدارة مباشرة (تقديم استئناف)' : 'Direct Support & Appeals Live Chat'}
                                </span>
                            </div>
                            <span style={{
                                fontSize: '11px',
                                color: '#10B981',
                                backgroundColor: 'rgba(16, 185, 129, 0.12)',
                                border: '1px solid rgba(16, 185, 129, 0.3)',
                                padding: '3px 8px',
                                borderRadius: '6px',
                                fontWeight: '700'
                            }}>
                                {rtl ? '● الدعم متصل' : '● Online Support'}
                            </span>
                        </div>

                        {/* Chat Messages Log */}
                        <div style={{
                            maxHeight: '260px',
                            minHeight: '120px',
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                            marginBottom: '14px',
                            padding: '12px',
                            backgroundColor: '#0B0F17',
                            borderRadius: '12px',
                            border: `1px solid ${BORDER_STROKE}`
                        }}>
                            {recentChatMessages.length === 0 ? (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '24px 10px',
                                    color: '#64748B',
                                    fontSize: '13px'
                                }}>
                                    {rtl ? 'اكتب رسالتك بالأسفل لفتح محادثة مباشرة مع الدعم الفني لحل الحظر فورياً 💬' : 'Type below to open a direct support chat to resolve this ban 💬'}
                                </div>
                            ) : (
                                recentChatMessages.map((msg, idx) => {
                                    const isSupport = msg.senderType === 'support' || msg.senderType === 'admin' || msg.senderType === 'system';
                                    const alignSelf = isSupport ? 'flex-start' : 'flex-end';
                                    const bubbleBg = isSupport ? '#1E293B' : '#047857';
                                    const borderColor = isSupport ? 'rgba(255,255,255,0.1)' : 'rgba(16,185,129,0.3)';
                                    const content = msg.message || msg.content || '';

                                    return (
                                        <div
                                            key={idx}
                                            style={{
                                                alignSelf,
                                                maxWidth: '85%',
                                                backgroundColor: bubbleBg,
                                                border: `1px solid ${borderColor}`,
                                                borderRadius: '12px',
                                                padding: '10px 14px',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                                textAlign: rtl ? 'right' : 'left'
                                            }}
                                        >
                                            <div style={{
                                                fontSize: '11px',
                                                fontWeight: '700',
                                                color: isSupport ? '#E2E8F0' : '#6EE7B7',
                                                marginBottom: '4px'
                                            }}>
                                                {isSupport 
                                                    ? (rtl ? '👑 إدارة لوكشفونو (الدعم الفني)' : '👑 Luxvono Technical Support') 
                                                    : (rtl ? 'أنت (التاجر) ✓✓' : 'You (Merchant) ✓✓')}
                                            </div>
                                            <div style={{
                                                fontSize: '14px',
                                                color: '#FFFFFF',
                                                lineHeight: '1.5',
                                                wordBreak: 'break-word'
                                            }}>
                                                {content}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                            <div ref={chatEndRef} />
                        </div>

                        {/* Quick Reply Form */}
                        <form onSubmit={handleSendReviewReply} style={{ display: 'flex', gap: '8px' }}>
                            <input
                                type="text"
                                value={chatReplyText}
                                onChange={(e) => setChatReplyText(e.target.value)}
                                placeholder={rtl ? 'اكتب رسالتك للدعم الفني لطلب مراجعة الحظر...' : 'Type message to support to request appeal...'}
                                style={{
                                    flex: 1,
                                    height: '46px',
                                    backgroundColor: '#0B0F17',
                                    border: `1px solid ${BORDER_STROKE}`,
                                    borderRadius: '10px',
                                    padding: '0 14px',
                                    color: '#FFFFFF',
                                    fontSize: '14px',
                                    outline: 'none'
                                }}
                            />
                            <button
                                type="submit"
                                disabled={isSendingChatReply || !chatReplyText.trim()}
                                style={{
                                    height: '46px',
                                    padding: '0 20px',
                                    backgroundColor: '#EF4444',
                                    color: '#FFFFFF',
                                    border: 'none',
                                    borderRadius: '10px',
                                    fontWeight: '800',
                                    fontSize: '14px',
                                    cursor: isSendingChatReply || !chatReplyText.trim() ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px',
                                    opacity: isSendingChatReply || !chatReplyText.trim() ? 0.6 : 1,
                                    boxShadow: '0 4px 14px rgba(239, 68, 68, 0.3)'
                                }}
                            >
                                <Send size={16} style={{ transform: rtl ? 'scaleX(-1)' : 'none' }} />
                                <span>{rtl ? 'إرسال' : 'Send'}</span>
                            </button>
                        </form>
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <a
                            href="mailto:support@luxvono.com?subject=استئناف حظر حساب التاجر"
                            style={{
                                width: '100%',
                                height: '46px',
                                backgroundColor: '#1A2333',
                                border: `1px solid ${BORDER_STROKE}`,
                                color: '#FFFFFF',
                                borderRadius: '10px',
                                fontWeight: '700',
                                fontSize: '13px',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                            }}
                        >
                            <Mail size={16} color="#38BDF8" />
                            <span>{rtl ? 'مراسلة الدعم عبر البريد: support@luxvono.com' : 'Email Support: support@luxvono.com'}</span>
                        </a>

                        <Link
                            to="/"
                            style={{
                                width: '100%',
                                height: '46px',
                                backgroundColor: '#131B2A',
                                border: `1px solid ${BORDER_STROKE}`,
                                color: '#CBD5E1',
                                borderRadius: '10px',
                                fontWeight: '700',
                                fontSize: '13px',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                            }}
                        >
                            <Home size={16} />
                            <span>{rtl ? 'العودة للمتجر الرئيسي كمشتري' : 'Return to Home Store'}</span>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // ─────────────────────────────────────────────────────────────
    // 2. PENDING / UNDER_REVIEW STATE (With Live Chat & Submitted Details)
    // ─────────────────────────────────────────────────────────────
    if (status === 'PENDING' || status === 'UNDER_REVIEW') {
        const displayMsgs = recentChatMessages.slice(-6);

        return (
            <div style={{
                minHeight: '100vh',
                backgroundColor: BG_COLOR,
                color: '#FFFFFF',
                direction: rtl ? 'rtl' : 'ltr',
                padding: '24px 16px 80px',
                display: 'flex',
                justifyContent: 'center'
            }}>
                <div style={{ maxWidth: '680px', width: '100%' }}>
                    {/* Top Home Navigation Bar */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '20px'
                    }}>
                        <Link
                            to="/"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                backgroundColor: '#1A2333',
                                border: `1px solid ${BORDER_STROKE}`,
                                borderRadius: '10px',
                                padding: '8px 16px',
                                color: '#FFFFFF',
                                textDecoration: 'none',
                                fontSize: '13px',
                                fontWeight: '700'
                            }}
                        >
                            <Home size={16} />
                            <span>{rtl ? 'العودة للشاشة الرئيسية' : 'Return to Home'}</span>
                        </Link>
                        <div style={{ fontSize: '13px', color: '#94A3B8', fontWeight: '600' }}>
                            LUXVONO REVIEW CENTER
                        </div>
                    </div>

                    {/* Header */}
                    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                        <div style={{
                            width: '68px',
                            height: '68px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(56, 189, 248, 0.12)',
                            border: `2px solid rgba(255, 255, 255, 0.2)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 14px'
                        }}>
                            <Clock size={34} color="#FFFFFF" />
                        </div>

                        <h1 style={{
                            fontSize: '24px',
                            fontWeight: '800',
                            color: '#FFFFFF',
                            marginBottom: '6px'
                        }}>
                            {rtl ? 'طلبك قيد المراجعة والتدقيق ⏳' : 'Application Under Review ⏳'}
                        </h1>
                        <p style={{ color: '#94A3B8', fontSize: '14px', margin: 0 }}>
                            {rtl ? `اسم المتجر: ${storeName}` : `Store: ${storeName}`}
                        </p>
                    </div>

                    {/* Audit Instructions Card */}
                    <div style={{
                        backgroundColor: DARK_CARD_BG,
                        border: `1px solid ${BORDER_STROKE}`,
                        borderRadius: '16px',
                        padding: '18px 20px',
                        marginBottom: '18px',
                        textAlign: rtl ? 'right' : 'left'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '8px'
                        }}>
                            <ShieldCheck size={20} color={EMERALD_COLOR} />
                            <h3 style={{
                                margin: 0,
                                fontSize: '14px',
                                fontWeight: '700',
                                color: '#FFFFFF'
                            }}>
                                {rtl ? 'فريق الامتثال والتدقيق يعمل على طلبك' : 'Compliance Team is Reviewing'}
                            </h3>
                        </div>
                        <p style={{
                            color: '#CBD5E1',
                            fontSize: '13px',
                            lineHeight: '1.7',
                            margin: 0
                        }}>
                            {rtl 
                                ? 'سيقوم فريق التدقيق والامتثال في Luxvono بمراجعة فواتيرك وموقع محلك التجاري. قد يتصل بك أحد موظفينا عبر اتصال فيديو مباشر للتحقق من المتجر، وسيظهر إشعار القبول فورياً على هذه الصفحة دون الحاجة لإعادة تحميلها.' 
                                : 'Our compliance team is actively reviewing your invoices and shop location. Staff may conduct a brief verification. You will be approved live on this page without refreshing.'}
                        </p>
                    </div>

                    {/* Submitted Details Card: اسم المتجر، عنوان المتجر، آخر فواتير */}
                    <div style={{
                        backgroundColor: DARK_CARD_BG,
                        border: `1px solid ${BORDER_STROKE}`,
                        borderRadius: '16px',
                        padding: '18px 20px',
                        marginBottom: '18px',
                        textAlign: rtl ? 'right' : 'left'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '14px',
                            borderBottom: `1px solid ${BORDER_STROKE}`,
                            paddingBottom: '10px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Store size={18} color="#FFFFFF" />
                                <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#FFFFFF' }}>
                                    {rtl ? 'بيانات المتجر والطلب المقدم' : 'Submitted Store Details'}
                                </h3>
                            </div>
                            <span style={{
                                fontSize: '11px',
                                color: '#E2E8F0',
                                backgroundColor: '#1E293B',
                                border: `1px solid ${BORDER_STROKE}`,
                                padding: '3px 8px',
                                borderRadius: '6px',
                                fontWeight: '700'
                            }}>
                                {rtl ? 'قيد التدقيق ⏳' : 'Under Review ⏳'}
                            </span>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ color: '#888888' }}>{rtl ? 'اسم المتجر / المعرض:' : 'Store Name:'}</span>
                                <span style={{ fontWeight: '700', color: '#FFFFFF' }}>{storeName || (rtl ? 'متجر معتمد' : 'Store')}</span>
                            </div>

                            {phoneNumber && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: '#888888' }}>{rtl ? 'رقم الهاتف للتواصل:' : 'Phone Number:'}</span>
                                    <span style={{ fontWeight: '600', color: '#FFFFFF', direction: 'ltr' }}>{phoneNumber}</span>
                                </div>
                            )}

                            {(storeLocationDesc || (latitude && longitude)) && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                                    <span style={{ color: '#888888' }}>{rtl ? 'عنوان وموقع المتجر:' : 'Store Address:'}</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span style={{ color: '#E2E8F0' }}>{storeLocationDesc || (rtl ? 'موقع GPS محدد' : 'Pinned Location')}</span>
                                        {latitude && longitude && (
                                            <a 
                                                href={mapLocationUrl || `https://www.google.com/maps?q=${latitude},${longitude}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: '#38BDF8', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '2px', fontSize: '12px', fontWeight: '700' }}
                                            >
                                                <span>({rtl ? 'الخريطة 📍' : 'Map 📍'})</span>
                                            </a>
                                        )}
                                    </div>
                                </div>
                            )}

                            {invoiceItems.length > 0 && (
                                <div style={{ marginTop: '8px' }}>
                                    <div style={{ color: '#888888', marginBottom: '8px' }}>
                                        {rtl ? `صور آخر فواتير بيع للإثبات (${invoiceItems.length} صور):` : `Recent Sales Invoices (${invoiceItems.length}):`}
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
                                        {invoiceItems.map((inv) => (
                                            <div 
                                                key={inv.id}
                                                onClick={() => setPreviewingInvoiceUrl(inv.previewUrl || inv.remoteUrl)}
                                                style={{
                                                    width: '64px',
                                                    height: '64px',
                                                    borderRadius: '8px',
                                                    overflow: 'hidden',
                                                    border: `1px solid ${BORDER_STROKE}`,
                                                    cursor: 'pointer',
                                                    flexShrink: 0,
                                                    backgroundColor: '#121212'
                                                }}
                                            >
                                                <img 
                                                    src={inv.previewUrl || inv.remoteUrl} 
                                                    alt="Invoice thumbnail" 
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Interactive Live Review Chat Card */}
                    <div style={{
                        backgroundColor: DARK_CARD_BG,
                        border: `1px solid ${BORDER_STROKE}`,
                        borderRadius: '16px',
                        padding: '18px 20px',
                        marginBottom: '18px'
                    }}>
                        {/* Live Review Header */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            paddingBottom: '12px',
                            borderBottom: `1px solid ${BORDER_STROKE}`,
                            marginBottom: '14px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    backgroundColor: EMERALD_COLOR,
                                    boxShadow: `0 0 10px ${EMERALD_COLOR}`
                                }} />
                                <span style={{
                                    fontSize: '14px',
                                    fontWeight: '700',
                                    color: '#FFFFFF'
                                }}>
                                    {rtl ? 'المحادثة المباشرة مع مسؤولي المراجعة 💬' : 'Live Review Team Chat 💬'}
                                </span>
                            </div>

                            {recentChatMessages.length > 0 && (
                                <span style={{
                                    backgroundColor: '#1E293B',
                                    border: `1px solid ${BORDER_STROKE}`,
                                    color: '#CBD5E1',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    padding: '3px 10px',
                                    borderRadius: '12px'
                                }}>
                                    {recentChatMessages.length} {rtl ? 'رسائل' : 'msgs'}
                                </span>
                            )}
                        </div>

                        {/* Message List */}
                        <div style={{
                            minHeight: '160px',
                            maxHeight: '340px',
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                            marginBottom: '16px',
                            padding: '4px'
                        }}>
                            {recentChatMessages.length === 0 ? (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flex: 1,
                                    padding: '32px 16px',
                                    textAlign: 'center',
                                    color: '#888888',
                                    fontSize: '13px'
                                }}>
                                    {rtl 
                                        ? 'لا توجد رسائل جديدة بعد. أي استفسار أو طلب من إدارة المراجعة سيظهر هنا فوراً ⚡' 
                                        : 'No messages yet. Admin review inquiries will appear here live.'}
                                </div>
                            ) : (
                                displayMsgs.map((msg, idx) => {
                                    const isSupport = msg.senderType === 'support' || msg.senderType === 'admin';
                                    const content = msg.content || msg.message || '';
                                    const bubbleBg = isSupport ? '#1A2333' : EMERALD_DARK;
                                    const borderColor = isSupport ? 'rgba(255, 255, 255, 0.12)' : 'rgba(16, 185, 129, 0.4)';
                                    const alignSelf = isSupport ? 'flex-start' : 'flex-end';

                                    return (
                                        <div
                                            key={idx}
                                            style={{
                                                alignSelf,
                                                maxWidth: '85%',
                                                backgroundColor: bubbleBg,
                                                border: `1px solid ${borderColor}`,
                                                borderRadius: '12px',
                                                padding: '10px 14px',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                                textAlign: rtl ? 'right' : 'left'
                                            }}
                                        >
                                            <div style={{
                                                fontSize: '11px',
                                                fontWeight: '700',
                                                color: isSupport ? '#E2E8F0' : '#6EE7B7',
                                                marginBottom: '4px'
                                            }}>
                                                {isSupport 
                                                    ? (rtl ? '👑 إدارة لوكشفونو (فريق المراجعة)' : '👑 Luxvono Review Team') 
                                                    : (rtl ? 'أنت (مقدم الطلب) ✓✓' : 'You (Applicant) ✓✓')}
                                            </div>
                                            <div style={{
                                                fontSize: '14px',
                                                color: '#FFFFFF',
                                                lineHeight: '1.5',
                                                wordBreak: 'break-word'
                                            }}>
                                                {content}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                            <div ref={chatEndRef} />
                        </div>

                        {/* Quick Reply Form */}
                        <form onSubmit={handleSendReviewReply} style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center'
                        }}>
                            <input
                                type="text"
                                value={chatReplyText}
                                onChange={(e) => setChatReplyText(e.target.value)}
                                placeholder={rtl ? 'اكتب ردك لفريق المراجعة هنا...' : 'Type your reply to review team...'}
                                style={{
                                    flex: 1,
                                    height: '46px',
                                    backgroundColor: '#0F172A',
                                    border: `1px solid ${BORDER_STROKE}`,
                                    borderRadius: '12px',
                                    padding: '0 16px',
                                    color: '#FFFFFF',
                                    fontSize: '14px',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                            />
                            <button
                                type="submit"
                                disabled={!chatReplyText.trim() || isSendingChatReply}
                                style={{
                                    width: '46px',
                                    height: '46px',
                                    borderRadius: '12px',
                                    backgroundColor: chatReplyText.trim() ? '#FFFFFF' : '#1E293B',
                                    border: 'none',
                                    color: chatReplyText.trim() ? '#000000' : '#888888',
                                    cursor: chatReplyText.trim() ? 'pointer' : 'default',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease',
                                    flexShrink: 0
                                }}
                            >
                                {isSendingChatReply ? (
                                    <div style={{
                                        width: '18px',
                                        height: '18px',
                                        border: '2px solid #000',
                                        borderTopColor: 'transparent',
                                        borderRadius: '50%',
                                        animation: 'spin 0.8s linear infinite'
                                    }} />
                                ) : (
                                    <Send size={18} style={{ transform: rtl ? 'scaleX(-1)' : 'none' }} />
                                )}
                            </button>
                        </form>
                    </div>

                    {/* Action buttons (Edit details + Return Home) */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <button
                            onClick={() => setStatus(null)}
                            style={{
                                width: '100%',
                                height: '46px',
                                backgroundColor: '#1A2333',
                                border: `1px solid ${BORDER_STROKE}`,
                                color: '#FFFFFF',
                                borderRadius: '10px',
                                fontWeight: '700',
                                fontSize: '13px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                            }}
                        >
                            <RefreshCw size={16} />
                            <span>{rtl ? 'تحديث أو تعديل بيانات وفواتير المتجر' : 'Edit Application Details & Invoices'}</span>
                        </button>

                        <Link
                            to="/"
                            style={{
                                width: '100%',
                                height: '46px',
                                backgroundColor: '#131B2A',
                                border: `1px solid ${BORDER_STROKE}`,
                                color: '#CBD5E1',
                                borderRadius: '10px',
                                fontWeight: '700',
                                fontSize: '13px',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                            }}
                        >
                            <Home size={16} />
                            <span>{rtl ? 'العودة للشاشة الرئيسية' : 'Return to Home Screen'}</span>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // ─────────────────────────────────────────────────────────────
    // 3. APPLICATION FORM (Clean Luxury - No Gold Lines)
    // ─────────────────────────────────────────────────────────────
    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: BG_COLOR,
            color: '#FFFFFF',
            direction: rtl ? 'rtl' : 'ltr',
            padding: '24px 16px 80px',
            display: 'flex',
            justifyContent: 'center'
        }}>
            <div style={{ maxWidth: '640px', width: '100%' }}>
                {/* Top Home Navigation Bar */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '20px'
                }}>
                    <Link
                        to="/"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            backgroundColor: '#1A2333',
                            border: `1px solid ${BORDER_STROKE}`,
                            borderRadius: '10px',
                            padding: '8px 16px',
                            color: '#FFFFFF',
                            textDecoration: 'none',
                            fontSize: '13px',
                            fontWeight: '700'
                        }}
                    >
                        <Home size={16} />
                        <span>{rtl ? 'العودة للشاشة الرئيسية' : 'Return to Home'}</span>
                    </Link>
                    <div style={{ fontSize: '13px', color: '#94A3B8', fontWeight: '600' }}>
                        LUXVONO OFFICIAL VENDOR
                    </div>
                </div>

                {/* Error Banner */}
                {errorMessage && (
                    <div style={{
                        backgroundColor: '#3B1212',
                        border: '1px solid #F87171',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        color: '#F87171',
                        fontSize: '14px',
                        marginBottom: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        <AlertCircle size={20} style={{ flexShrink: 0 }} />
                        <span>{errorMessage}</span>
                    </div>
                )}

                {/* Rejection Alert Card from Previous Review */}
                {rejectionReason && (
                    <div style={{
                        backgroundColor: RED_BG,
                        border: `1px solid ${RED_BORDER}`,
                        borderRadius: '16px',
                        padding: '16px',
                        marginBottom: '20px',
                        textAlign: rtl ? 'right' : 'left'
                    }}>
                        <div style={{
                            color: '#FCA5A5',
                            fontWeight: '700',
                            fontSize: '14px',
                            marginBottom: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <AlertCircle size={18} color="#F87171" />
                            <span>{rtl ? '⚠️ ملاحظة تدقيق الإدارة السابقة:' : '⚠️ Admin Review Feedback:'}</span>
                        </div>
                        <div style={{
                            color: '#FEE2E2',
                            fontSize: '13px',
                            lineHeight: '1.6',
                            marginBottom: '8px'
                        }}>
                            {rejectionReason}
                        </div>
                        <div style={{ color: '#CBD5E1', fontSize: '12px' }}>
                            {rtl 
                                ? 'يمكنك تصحيح البيانات والفواتير وإعادة الإرسال فورياً للمراجعة.' 
                                : 'Please update the required details and resubmit.'}
                        </div>
                    </div>
                )}

                {/* Simplified Quick Registration Header Card (Clean Luxury) */}
                <div style={{
                    backgroundColor: DARK_CARD_BG,
                    border: `1px solid ${BORDER_STROKE}`,
                    borderRadius: '16px',
                    padding: '18px 20px',
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px'
                }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        backgroundColor: '#1E293B',
                        border: `1px solid ${BORDER_STROKE}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                    }}>
                        <Store size={24} color="#FFFFFF" />
                    </div>
                    <div>
                        <h2 style={{
                            margin: '0 0 4px',
                            fontSize: '16px',
                            fontWeight: '800',
                            color: '#FFFFFF'
                        }}>
                            {rtl ? 'تسجيل بائع معتمد (فوري وميسر) ✨' : 'Instant Verified Seller Registration ✨'}
                        </h2>
                        <p style={{
                            margin: 0,
                            fontSize: '12px',
                            color: '#94A3B8',
                            lineHeight: '1.5'
                        }}>
                            {rtl 
                                ? 'فقط 4 بيانات أساسية بدون تعقيدات السجل التجاري أو الحساب البنكي.' 
                                : 'Only 4 essential steps. No commercial registration or bank details needed.'}
                        </p>
                    </div>
                </div>

                {/* FORM FIELDS */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                    
                    {/* Field 1: Store Name */}
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: '14px',
                            fontWeight: '700',
                            color: '#FFFFFF',
                            marginBottom: '8px'
                        }}>
                            {rtl ? '1. اسم المتجر / المعرض التجاري *' : '1. Store / Shop Name *'}
                        </label>
                        <div style={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                            <div style={{
                                position: 'absolute',
                                [rtl ? 'right' : 'left']: '14px',
                                color: '#94A3B8',
                                pointerEvents: 'none'
                            }}>
                                <Store size={20} />
                            </div>
                            <input
                                type="text"
                                value={storeName}
                                onChange={(e) => setStoreName(e.target.value)}
                                placeholder={rtl ? 'مثال: مجوهرات الأناقة الملكية' : 'e.g. Elegance Royal Jewels'}
                                style={{
                                    width: '100%',
                                    height: '50px',
                                    backgroundColor: '#111622',
                                    border: `1px solid ${BORDER_STROKE}`,
                                    borderRadius: '12px',
                                    padding: rtl ? '0 46px 0 16px' : '0 16px 0 46px',
                                    color: '#FFFFFF',
                                    fontSize: '15px',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                    </div>

                    {/* Field 2: Personal Mobile Number */}
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: '14px',
                            fontWeight: '700',
                            color: '#FFFFFF',
                            marginBottom: '8px'
                        }}>
                            {rtl ? '2. رقم الهاتف الخاص للتواصل المباشر *' : '2. Personal Mobile Number *'}
                        </label>
                        <div style={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                            <div style={{
                                position: 'absolute',
                                [rtl ? 'right' : 'left']: '14px',
                                color: '#94A3B8',
                                pointerEvents: 'none'
                            }}>
                                <Phone size={20} />
                            </div>
                            <input
                                type="tel"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                                placeholder={rtl ? 'مثال: 0501234567 أو +966501234567' : 'e.g. +966501234567'}
                                style={{
                                    width: '100%',
                                    height: '50px',
                                    backgroundColor: '#111622',
                                    border: `1px solid ${BORDER_STROKE}`,
                                    borderRadius: '12px',
                                    padding: rtl ? '0 46px 0 16px' : '0 16px 0 46px',
                                    color: '#FFFFFF',
                                    fontSize: '15px',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                    direction: 'ltr',
                                    textAlign: rtl ? 'right' : 'left'
                                }}
                            />
                        </div>
                    </div>

                    {/* Field 3: GPS Pin Location on Map */}
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: '14px',
                            fontWeight: '700',
                            color: '#FFFFFF',
                            marginBottom: '8px'
                        }}>
                            {rtl ? '3. تحديد موقع المحل على الخريطة (GPS) 📍' : '3. Pin Shop Location on Map (GPS) 📍'}
                        </label>

                        <div style={{
                            backgroundColor: DARK_CARD_BG,
                            border: `1px solid ${BORDER_STROKE}`,
                            borderRadius: '16px',
                            padding: '18px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <MapPin size={20} color="#FFFFFF" />
                                <span style={{ fontWeight: '700', fontSize: '14px', color: '#FFFFFF' }}>
                                    {rtl ? 'تحديد إحداثيات موقع المحل تلقائياً' : 'Pin Shop Location (GPS)'}
                                </span>
                            </div>

                            <p style={{
                                color: '#888888',
                                fontSize: '12px',
                                lineHeight: '1.5',
                                margin: 0
                            }}>
                                {rtl 
                                    ? 'حدد موقع محلك التجاري عبر GPS أو اكتب العنوان لتسهيل وصول الزبائن وإثبات وجود المحل الفعلي.' 
                                    : 'Pin your physical store location using GPS or type the address details.'}
                            </p>

                            {/* Coordinate Pinned Green Box */}
                            {latitude && longitude && (
                                <div style={{
                                    backgroundColor: EMERALD_DARK,
                                    borderRadius: '8px',
                                    padding: '10px 14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    color: '#A7F3D0',
                                    fontSize: '12px'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <CheckCircle2 size={16} color="#34D399" />
                                        <span>
                                            {rtl 
                                                ? `تم التقاط إحداثيات المحل بدقة: (${latitude.toFixed(4)}, ${longitude.toFixed(4)})` 
                                                : `Coordinates pinned: (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`}
                                        </span>
                                    </div>
                                    <a
                                        href={mapLocationUrl || `https://www.google.com/maps?q=${latitude},${longitude}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#34D399', textDecoration: 'underline' }}
                                    >
                                        <ExternalLink size={14} />
                                    </a>
                                </div>
                            )}

                            {/* GPS Button */}
                            <button
                                type="button"
                                onClick={handleCaptureLocation}
                                disabled={isLocating}
                                style={{
                                    height: '46px',
                                    backgroundColor: '#1E293B',
                                    border: `1px solid ${BORDER_STROKE}`,
                                    borderRadius: '8px',
                                    color: '#FFFFFF',
                                    fontWeight: '700',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {isLocating ? (
                                    <>
                                        <div style={{
                                            width: '16px',
                                            height: '16px',
                                            border: '2px solid #FFFFFF',
                                            borderTopColor: 'transparent',
                                            borderRadius: '50%',
                                            animation: 'spin 0.8s linear infinite'
                                        }} />
                                        <span>{rtl ? 'جاري تحديد الموقع...' : 'Locating...'}</span>
                                    </>
                                ) : (
                                    <>
                                        <Navigation size={16} />
                                        <span>
                                            {latitude 
                                                ? (rtl ? 'إعادة تحديث موقع GPS' : 'Refresh GPS Location') 
                                                : (rtl ? '📍 تحديد موقع المحل على الخريطة الآن' : 'Pin Shop on Map Now')}
                                        </span>
                                    </>
                                )}
                            </button>

                            {/* Street / Landmark input */}
                            <input
                                type="text"
                                value={storeLocationDesc}
                                onChange={(e) => setStoreLocationDesc(e.target.value)}
                                placeholder={rtl ? 'وصف العنوان أو الشارع أو المعلم القريب (اختياري)' : 'Address / Street / Landmark (Optional)'}
                                style={{
                                    height: '44px',
                                    backgroundColor: '#111622',
                                    border: `1px solid ${BORDER_STROKE}`,
                                    borderRadius: '8px',
                                    padding: '0 14px',
                                    color: '#FFFFFF',
                                    fontSize: '13px',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                    </div>

                    {/* Field 4: Sales Invoices Proof */}
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: '14px',
                            fontWeight: '700',
                            color: '#FFFFFF',
                            marginBottom: '8px'
                        }}>
                            {rtl 
                                ? `4. صور آخر فواتير بيع للإثبات والتحقق * (${recentInvoicesMonth})` 
                                : `4. Recent Sales Invoices for Proof * (${recentInvoicesMonth})`}
                        </label>

                        <div style={{
                            backgroundColor: DARK_CARD_BG,
                            border: `1px solid ${BORDER_STROKE}`,
                            borderRadius: '16px',
                            padding: '18px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '14px'
                        }}>
                            <p style={{
                                color: '#888888',
                                fontSize: '12px',
                                lineHeight: '1.5',
                                margin: 0
                            }}>
                                {rtl 
                                    ? 'يرجى تصوير أو إرفاق صور واضحة لآخر فواتير مبيعات صادرة من المحل للإثبات والتحقق السريع واعتماد حسابك.' 
                                    : 'Upload clear photos of recent sales invoices issued by your shop for quick verification.'}
                            </p>

                            {/* Thumbnails Row / Grid */}
                            {invoiceItems.length > 0 && (
                                <div style={{
                                    display: 'flex',
                                    gap: '12px',
                                    overflowX: 'auto',
                                    paddingBottom: '6px'
                                }}>
                                    {invoiceItems.map((item) => {
                                        const borderColor = item.isSuccess 
                                            ? EMERALD_COLOR 
                                            : item.isError 
                                            ? '#EF4444' 
                                            : 'rgba(255, 255, 255, 0.3)';

                                        return (
                                            <div
                                                key={item.id}
                                                style={{
                                                    position: 'relative',
                                                    width: '110px',
                                                    height: '110px',
                                                    flexShrink: 0,
                                                    borderRadius: '12px',
                                                    border: `1.5px solid ${borderColor}`,
                                                    overflow: 'hidden',
                                                    backgroundColor: '#121212',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => setPreviewingInvoiceUrl(item.previewUrl || item.remoteUrl)}
                                            >
                                                <img
                                                    src={item.previewUrl || item.remoteUrl}
                                                    alt="Invoice"
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover'
                                                    }}
                                                />

                                                {/* Uploading Spinner Overlay */}
                                                {item.isUploading && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        backgroundColor: 'rgba(0,0,0,0.65)',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '4px'
                                                    }}>
                                                        <div style={{
                                                            width: '20px',
                                                            height: '20px',
                                                            border: '2px solid #FFFFFF',
                                                            borderTopColor: 'transparent',
                                                            borderRadius: '50%',
                                                            animation: 'spin 0.8s linear infinite'
                                                        }} />
                                                        <span style={{ fontSize: '9px', fontWeight: '700', color: '#fff' }}>
                                                            {rtl ? 'جاري الرفع...' : 'Uploading...'}
                                                        </span>
                                                    </div>
                                                )}

                                                {/* Success Badge */}
                                                {item.isSuccess && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        bottom: 0,
                                                        left: 0,
                                                        right: 0,
                                                        backgroundColor: 'rgba(16, 185, 129, 0.9)',
                                                        color: '#FFFFFF',
                                                        fontSize: '9px',
                                                        fontWeight: '700',
                                                        padding: '2px 0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '3px'
                                                    }}>
                                                        <Check size={11} />
                                                        <span>{rtl ? 'تم الرفع' : 'Uploaded'}</span>
                                                    </div>
                                                )}

                                                {/* Error Badge with Retry */}
                                                {item.isError && (
                                                    <div 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRetryUpload(item);
                                                        }}
                                                        style={{
                                                            position: 'absolute',
                                                            bottom: 0,
                                                            left: 0,
                                                            right: 0,
                                                            backgroundColor: 'rgba(239, 68, 68, 0.9)',
                                                            color: '#FFFFFF',
                                                            fontSize: '9px',
                                                            fontWeight: '700',
                                                            padding: '2px 0',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: '3px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        <RefreshCw size={10} />
                                                        <span>{rtl ? 'إعادة الرفع' : 'Retry'}</span>
                                                    </div>
                                                )}

                                                {/* Delete Corner Button */}
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemoveInvoice(item.id);
                                                    }}
                                                    style={{
                                                        position: 'absolute',
                                                        top: '4px',
                                                        [rtl ? 'left' : 'right']: '4px',
                                                        width: '24px',
                                                        height: '24px',
                                                        borderRadius: '50%',
                                                        backgroundColor: 'rgba(0, 0, 0, 0.75)',
                                                        border: 'none',
                                                        color: '#FFFFFF',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        cursor: 'pointer',
                                                        padding: 0
                                                    }}
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Summary Count Line */}
                            {invoiceItems.length > 0 && (
                                <div style={{
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    color: invoiceItems.some(i => i.isSuccess) ? '#34D399' : '#CBD5E1'
                                }}>
                                    {rtl 
                                        ? `تم تحديد ${invoiceItems.length} صور فواتير (${invoiceItems.filter(i => i.isSuccess || i.remoteUrl).length} تم رفعها بنجاح ✓)` 
                                        : `${invoiceItems.length} invoices selected (${invoiceItems.filter(i => i.isSuccess || i.remoteUrl).length} uploaded ✓)`}
                                </div>
                            )}

                            {/* Hidden file input */}
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleSelectInvoices}
                            />

                            {/* Add Photos Button */}
                            <button
                                type="button"
                                disabled={isUploadingInvoices}
                                onClick={() => fileInputRef.current?.click()}
                                style={{
                                    height: '48px',
                                    backgroundColor: '#1E293B',
                                    border: `1px solid ${BORDER_STROKE}`,
                                    borderRadius: '10px',
                                    color: '#FFFFFF',
                                    fontWeight: '700',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {isUploadingInvoices ? (
                                    <>
                                        <div style={{
                                            width: '16px',
                                            height: '16px',
                                            border: '2px solid #FFFFFF',
                                            borderTopColor: 'transparent',
                                            borderRadius: '50%',
                                            animation: 'spin 0.8s linear infinite'
                                        }} />
                                        <span>{rtl ? 'جاري رفع الفواتير...' : 'Uploading invoices...'}</span>
                                    </>
                                ) : (
                                    <>
                                        <Camera size={18} />
                                        <span>
                                            {invoiceItems.length === 0 
                                                ? (rtl ? 'إرفاق صور فواتير المبيعات الأخيرة 📸' : 'Attach Sales Invoices Images 📸') 
                                                : (rtl ? 'إضافة المزيد من صور الفواتير +' : 'Add More Invoices +')}
                                        </span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Optional Demographics (Gender & Age) */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '12px'
                    }}>
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '13px',
                                fontWeight: '600',
                                color: '#9CA3AF',
                                marginBottom: '6px'
                            }}>
                                {rtl ? 'الجنس (اختياري)' : 'Gender (Optional)'}
                            </label>
                            <select
                                value={applicantGender}
                                onChange={(e) => setApplicantGender(e.target.value)}
                                style={{
                                    width: '100%',
                                    height: '44px',
                                    backgroundColor: '#111622',
                                    border: `1px solid ${BORDER_STROKE}`,
                                    borderRadius: '10px',
                                    padding: '0 12px',
                                    color: '#FFFFFF',
                                    fontSize: '13px',
                                    outline: 'none'
                                }}
                            >
                                <option value="MALE">{rtl ? 'ذكر' : 'Male'}</option>
                                <option value="FEMALE">{rtl ? 'أنثى' : 'Female'}</option>
                            </select>
                        </div>

                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '13px',
                                fontWeight: '600',
                                color: '#9CA3AF',
                                marginBottom: '6px'
                            }}>
                                {rtl ? 'العمر (اختياري)' : 'Age (Optional)'}
                            </label>
                            <input
                                type="number"
                                value={applicantAge}
                                onChange={(e) => setApplicantAge(e.target.value)}
                                placeholder={rtl ? 'مثال: 32' : 'e.g. 32'}
                                style={{
                                    width: '100%',
                                    height: '44px',
                                    backgroundColor: '#111622',
                                    border: `1px solid ${BORDER_STROKE}`,
                                    borderRadius: '10px',
                                    padding: '0 12px',
                                    color: '#FFFFFF',
                                    fontSize: '13px',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                    </div>

                    {/* Submit Button (Clean Modern Luxury) */}
                    <button
                        type="button"
                        onClick={handleSubmitApplication}
                        disabled={isSubmitting}
                        style={{
                            width: '100%',
                            height: '54px',
                            backgroundColor: '#FFFFFF',
                            color: '#0A0F1A',
                            border: 'none',
                            borderRadius: '12px',
                            fontWeight: '800',
                            fontSize: '16px',
                            cursor: isSubmitting ? 'default' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px',
                            marginTop: '10px',
                            boxShadow: '0 8px 24px rgba(255, 255, 255, 0.15)',
                            transition: 'all 0.25s ease'
                        }}
                    >
                        {isSubmitting ? (
                            <div style={{
                                width: '22px',
                                height: '22px',
                                border: '3px solid #000',
                                borderTopColor: 'transparent',
                                borderRadius: '50%',
                                animation: 'spin 0.8s linear infinite'
                            }} />
                        ) : (
                            <span>{rtl ? 'تقديم طلب الانضمام كبائع فوراً 🚀' : 'Submit Vendor Application Now 🚀'}</span>
                        )}
                    </button>

                    {/* Return to Home Screen button */}
                    <Link
                        to="/"
                        style={{
                            width: '100%',
                            height: '46px',
                            backgroundColor: '#131B2A',
                            border: `1px solid ${BORDER_STROKE}`,
                            color: '#CBD5E1',
                            borderRadius: '12px',
                            fontWeight: '700',
                            fontSize: '14px',
                            textDecoration: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}
                    >
                        <Home size={16} />
                        <span>{rtl ? 'العودة للشاشة الرئيسية' : 'Return to Home Screen'}</span>
                    </Link>
                </div>
            </div>

            {/* LIVE APPROVAL CELEBRATION MODAL (Instant live detection without reload) */}
            {showLiveApprovedModal && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.85)',
                    backdropFilter: 'blur(8px)',
                    zIndex: 10000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }}>
                    <div style={{
                        maxWidth: '520px',
                        width: '100%',
                        backgroundColor: '#131B2A',
                        border: `1.5px solid ${EMERALD_COLOR}`,
                        borderRadius: '24px',
                        padding: '36px 28px',
                        textAlign: 'center',
                        color: '#FFFFFF',
                        boxShadow: '0 25px 60px rgba(0,0,0,0.85), 0 0 35px rgba(16, 185, 129, 0.25)',
                        animation: 'fadeInUp 0.35s ease'
                    }}>
                        <div style={{
                            width: '84px',
                            height: '84px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(16, 185, 129, 0.15)',
                            border: `2px solid ${EMERALD_COLOR}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 20px',
                            boxShadow: '0 0 32px rgba(16, 185, 129, 0.35)'
                        }}>
                            <CheckCircle size={48} color={EMERALD_COLOR} />
                        </div>

                        <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            backgroundColor: 'rgba(16, 185, 129, 0.15)',
                            color: '#34D399',
                            padding: '4px 14px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: '700',
                            marginBottom: '14px'
                        }}>
                            <BellRing size={14} />
                            <span>{rtl ? 'إشعار مباشر وفوري' : 'Live Instant Notification'}</span>
                        </div>

                        <h2 style={{
                            fontSize: '24px',
                            fontWeight: '800',
                            color: '#FFFFFF',
                            marginBottom: '10px'
                        }}>
                            {rtl ? '🎉 تم قبولك! تم اعتماد حسابك كتاجر رسمي بنجاح!' : '🎉 Congratulations! Your Vendor Account is Approved!'}
                        </h2>

                        <p style={{
                            color: '#CBD5E1',
                            fontSize: '14px',
                            lineHeight: '1.7',
                            marginBottom: '28px'
                        }}>
                            {rtl 
                                ? 'قام فريق الامتثال والإدارة باعتماد طلب متجرك الآن فورياً. تم تفعيل كامل صلاحيات التاجر لحسابك ويمكنك البدء في نشر المنتجات وإدارة المخزون مباشرة.' 
                                : 'Our compliance team has just approved your vendor application live! Your merchant studio is fully unlocked.'}
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowLiveApprovedModal(false);
                                    navigate('/seller/dashboard');
                                }}
                                style={{
                                    height: '52px',
                                    backgroundColor: '#FFFFFF',
                                    color: '#0A0F1A',
                                    border: 'none',
                                    borderRadius: '12px',
                                    fontWeight: '800',
                                    fontSize: '16px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    boxShadow: '0 8px 24px rgba(255, 255, 255, 0.2)'
                                }}
                            >
                                <Store size={20} />
                                <span>{rtl ? 'الدخول للوحة تحكم التاجر الآن 🚀' : 'Go to Vendor Dashboard Now 🚀'}</span>
                            </button>

                            <button
                                type="button"
                                onClick={() => {
                                    setShowLiveApprovedModal(false);
                                    navigate('/');
                                }}
                                style={{
                                    height: '46px',
                                    backgroundColor: '#1E293B',
                                    color: '#CBD5E1',
                                    border: `1px solid ${BORDER_STROKE}`,
                                    borderRadius: '12px',
                                    fontWeight: '700',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px'
                                }}
                            >
                                <Home size={16} />
                                <span>{rtl ? 'العودة للشاشة الرئيسية' : 'Return to Home Screen'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Full-Screen Image Preview Modal */}
            {previewingInvoiceUrl && (
                <div 
                    onClick={() => setPreviewingInvoiceUrl(null)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.88)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '16px'
                    }}
                >
                    <div 
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            maxWidth: '560px',
                            width: '100%',
                            backgroundColor: '#131B2A',
                            border: `1px solid ${BORDER_STROKE}`,
                            borderRadius: '16px',
                            padding: '20px',
                            boxShadow: '0 24px 60px rgba(0,0,0,0.8)'
                        }}
                    >
                        {/* Header */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '16px'
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: '16px',
                                fontWeight: '700',
                                color: '#FFFFFF'
                            }}>
                                {rtl ? 'معاينة فاتورة المبيعات' : 'Invoice Photo Preview'}
                            </h3>
                            <button
                                type="button"
                                onClick={() => setPreviewingInvoiceUrl(null)}
                                style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    backgroundColor: '#1E293B',
                                    border: 'none',
                                    color: '#FFFFFF',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer'
                                }}
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Image Container */}
                        <div style={{
                            width: '100%',
                            maxHeight: '440px',
                            backgroundColor: '#000000',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '16px'
                        }}>
                            <img
                                src={previewingInvoiceUrl}
                                alt="Invoice full preview"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '440px',
                                    objectFit: 'contain'
                                }}
                            />
                        </div>

                        {/* Close button */}
                        <button
                            type="button"
                            onClick={() => setPreviewingInvoiceUrl(null)}
                            style={{
                                width: '100%',
                                height: '44px',
                                backgroundColor: '#FFFFFF',
                                color: '#000000',
                                border: 'none',
                                borderRadius: '10px',
                                fontWeight: '700',
                                fontSize: '14px',
                                cursor: 'pointer'
                            }}
                        >
                            {rtl ? 'إغلاق المعاينة' : 'Close Preview'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
